import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useContext, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useCreateProfessionalRulesetMutation,
  useCreateManyProfessionalRulesetsMutation,
} from "store/queries/professionalRuleset";

import {
  IProfessionalRulesetFormData,
  INITIAL_PROFESSIONAL_FORM_DATA,
  ProfessionalRulesetForm,
} from "./components/ProfessionalRulesetForm";

export function CreateProfessionalRulesetPage() {
  useDocumentTitle("Create Professional Ruleset");
  const navigate = useNavigate();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const [createManyMutation, { isLoading }] =
    useCreateManyProfessionalRulesetsMutation();
  // CLE-2597 — a fallback is a single row with no provider, so the N×M fan-out endpoint does
  // not apply to it; it goes through the plain create instead.
  const [createMutation, { isLoading: isCreatingFallback }] =
    useCreateProfessionalRulesetMutation();
  // CLE-2597 — prefilled by the contract view's "Configure" link; see the
  // institutional create page for the same treatment.
  const [searchParams] = useSearchParams();
  const prefillProviderId = searchParams.get("providerId");
  const prefillPayerId = Number(searchParams.get("payerId"));
  const [formData, setFormData] = useState<IProfessionalRulesetFormData>({
    ...INITIAL_PROFESSIONAL_FORM_DATA,
    providerIds: prefillProviderId ? [prefillProviderId] : [],
    payerIds:
      Number.isFinite(prefillPayerId) && prefillPayerId > 0
        ? [prefillPayerId]
        : [],
  });

  const handleSubmit = async () => {
    const isFallback = formData.applicability === "FALLBACK";
    if (
      !isFallback &&
      (!formData.providerIds.length || !formData.payerIds.length)
    )
      return;
    setIsLoading(true);
    try {
      if (isFallback) {
        await createMutation({
          applicability: "FALLBACK",
          // null = the system-wide default; a value = that payer across every provider.
          payerId: formData.payerId ?? null,
          compoundBilateralAndMppr: formData.compoundBilateralAndMppr,
          endoApplies: formData.endoApplies,
          ncciApplies: formData.ncciApplies,
          mueApplies: formData.mueApplies,
          disabledEndoBaseCptCodes: formData.disabledEndoBaseCptCodes,
          primarySelectionMethod: formData.primarySelectionMethod,
        }).unwrap();
        toastElement?.show({
          severity: "success",
          summary: "Created!",
          detail:
            formData.payerId == null
              ? "System-wide fallback rule created."
              : "Payer fallback rule created.",
        });
        navigate("/payer-rules");
        return;
      }
      const created = await createManyMutation({
        providerIds: formData.providerIds,
        payerIds: formData.payerIds,
        compoundBilateralAndMppr: formData.compoundBilateralAndMppr,
        endoApplies: formData.endoApplies,
        ncciApplies: formData.ncciApplies,
        mueApplies: formData.mueApplies,
        disabledEndoBaseCptCodes: formData.disabledEndoBaseCptCodes,
        primarySelectionMethod: formData.primarySelectionMethod,
      }).unwrap();
      const count = created.data?.length ?? 0;
      toastElement?.show({
        severity: "success",
        summary: "Created!",
        detail: `${count} professional ruleset${
          count === 1 ? "" : "s"
        } created successfully.`,
      });
      navigate("/payer-rules");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          error?.data?.message || "Failed to create professional rulesets",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ProfessionalRulesetForm
      title="Create Professional Rulesets"
      submitLabel="Create Rulesets"
      formData={formData}
      onChange={setFormData}
      onSubmit={handleSubmit}
      isSubmitting={isLoading || isCreatingFallback}
      multiScope
    />
  );
}
